import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db, storage } from "../config/firebase";
import { collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, limit, doc, setDoc, deleteDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { motion, useAnimation, useMotionValue, useTransform } from "framer-motion";
import { Send, Smile, Image as ImageIcon, Loader2, ArrowLeft, MessageSquare, MoreVertical, Trash2, Pin, X, Reply, Star, Info, Palette } from "lucide-react";
import chatPattern from "../assets/wallpapers/chat-pattern.svg";
import EWave from "../assets/wallpapers/EWave.svg";
import SND from "../assets/wallpapers/SND.svg";
import SNR from "../assets/wallpapers/SNR.svg";
import SNRBO from "../assets/wallpapers/SNRBO.svg";
import SUPPLY from "../assets/wallpapers/SUPPLY.svg";
import VNW2N01 from "../assets/wallpapers/VNW2N01.svg";
import Wave from "../assets/wallpapers/wave.svg";
import Crypto from "../assets/wallpapers/Crypto.svg";
import Candle from "../assets/wallpapers/candlestick-doodles.svg";
import Demand from "../assets/wallpapers/dEMAND.svg";
import MarketStructure from "../assets/wallpapers/2RgXJ01.svg";
import TradingLevels from "../assets/wallpapers/trading-levels.svg";

// Helper function to format date
const formatDate = (date) => {
    if (!date) return '';
    const now = new Date();
    const msgDate = date.toDate ? date.toDate() : new Date(date);

    const isToday = msgDate.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = msgDate.toDateString() === yesterday.toDateString();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';

    return msgDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: msgDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
};

// Helper function to format time
const formatTime = (date) => {
    if (!date) return '';
    const msgDate = date.toDate ? date.toDate() : new Date(date);
    return msgDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
};

const wallpapers = [
    { id: 'default', name: 'Bull & Bear', src: chatPattern },
    { id: 'wave', name: 'Wave Analysis', src: Wave },
    { id: 'ewave', name: 'Elliott Wave', src: EWave },
    { id: 'snd', name: 'Supply & Demand', src: SND },
    { id: 'snr', name: 'Support & Res', src: SNR },
    { id: 'supply', name: 'Supply Zones', src: SUPPLY },
    { id: 'vnw', name: 'VNW Pattern', src: VNW2N01 },
    { id: 'crypto', name: 'Crypto', src: Crypto },
    { id: 'candle', name: 'Candlesticks', src: Candle },
    { id: 'market', name: 'Market Structure', src: MarketStructure },
    { id: 'demand', name: 'Demand Zones', src: Demand },
    { id: 'levels', name: 'Trading Levels', src: TradingLevels },
];

export default function Chat() {
    const navigate = useNavigate();
    const { user } = useAuth() || { user: null };
    const [messages, setMessages] = useState([]);
    const [messageContent, setMessageContent] = useState("");
    const [showEmoji, setShowEmoji] = useState(false);
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);

    // Image Upload States
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);

    // Admin features states
    const [showMessageMenu, setShowMessageMenu] = useState(null);
    const [pinnedMessage, setPinnedMessage] = useState(null);
    const [showWallpaperMenu, setShowWallpaperMenu] = useState(false);
    const [currentWallpaper, setCurrentWallpaper] = useState(() => {
        return localStorage.getItem('chat_wallpaper') || wallpapers[0].src;
    });

    const handleWallpaperChange = (src) => {
        setCurrentWallpaper(src);
        localStorage.setItem('chat_wallpaper', src);
        setShowWallpaperMenu(false);
    };

    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const menuRef = useRef(null);

    const isAdmin = user?.role === 'admin';

    // Subscribe to Firestore messages
    // Close menu when clicking outside - Added Effect
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMessageMenu(null);
            }
        };

        if (showMessageMenu) {
            document.addEventListener("click", handleClickOutside);
        }
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [showMessageMenu]);

    useEffect(() => {
        const messagesRef = collection(db, "community_chat");
        const q = query(messagesRef, orderBy("created_at", "asc"), limit(100));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const newMessages = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setMessages(newMessages);

            // Check for pinned message
            const pinned = newMessages.find(m => m.pinned);
            setPinnedMessage(pinned || null);

            // Play notification sound if new message arrived (and it's not from current user)
            if (newMessages.length > 0) {
                const latestMsg = newMessages[newMessages.length - 1];
                // Check if it's a new message (based on time or just length change detection)
                // Since this runs on snapshots, we rely on the length growing or ID check in a real scenario.
                // For simplicity here, we assume if standard snapshot updates and it's not me, play sound.
                // NOTE: This might play on initial load, so we can add a check for 'loading' state or timestamp.
            }
            // Simple robust check:
            const lastMsg = newMessages[newMessages.length - 1];
            if (!loading && lastMsg && lastMsg.author_email !== user?.email) {
                const audio = new Audio("https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3");
                audio.volume = 0.5;
                audio.play().catch(e => console.log("Audio play failed (interaction needed):", e));
            }

            setLoading(false);

            setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            }, 100);
        }, (error) => {
            console.error("Error fetching messages:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Mark messages as read when page is opened
    useEffect(() => {
        if (!user?.email) return;

        const markAsRead = async () => {
            try {
                const userReadRef = doc(db, 'user_read_status', `${user.email}_community_chat`);
                await setDoc(userReadRef, {
                    last_read: serverTimestamp(),
                    user_email: user.email
                }, { merge: true });
            } catch (error) {
                console.error('Error marking as read:', error);
            }
        };

        markAsRead();
    }, [user?.email]);

    const [replyingTo, setReplyingTo] = useState(null); // { id: messageId, author: authorName, content: content }

    // Admin toggles verification
    const handleVerifyUser = async (userEmail, currentStatus) => {
        if (!isAdmin) return;
        // Mock verification logic
        alert("Verification feature requires user database update.");
    };

    const [editingMessage, setEditingMessage] = useState(null);

    // Edit Message Handler
    const handleEditClick = (message) => {
        setEditingMessage({
            id: message.id,
            content: message.content
        });
        setMessageContent(message.content);
        document.getElementById("chat-input")?.focus();
        setShowMessageMenu(null);
    };

    const handleCancelEdit = () => {
        setEditingMessage(null);
        setMessageContent("");
    };

    // Reply to message
    const handleReply = (message) => {
        setReplyingTo({
            id: message.id,
            author: message.author_name,
            content: message.content
        });
        document.getElementById("chat-input")?.focus();
    };

    // Handle File Selection
    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { // 5MB limit
                alert("File too large. Max 5MB.");
                return;
            }
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSend = async () => {
        if ((!messageContent.trim() && !imageFile) || sending) return;

        setSending(true);
        try {
            let imageUrl = null;

            // Upload Image if present
            if (imageFile) {
                const imageRef = ref(storage, `chat_images/${Date.now()}_${imageFile.name}`);
                await uploadBytes(imageRef, imageFile);
                imageUrl = await getDownloadURL(imageRef);
            }

            if (editingMessage) {
                // UPDATE existing message
                await updateDoc(doc(db, "community_chat", editingMessage.id), {
                    content: messageContent,
                    is_edited: true,
                    updated_at: serverTimestamp(),
                    // Note: Usually we don't update images in edit to keep it simple, 
                    // or we could add logic here. For now, sticking to content edit.
                });
                setEditingMessage(null);
            } else {
                // CREATE new message
                await addDoc(collection(db, "community_chat"), {
                    content: messageContent,
                    imageUrl: imageUrl, // Add image URL
                    author_name: user?.firstName || user?.email?.split('@')[0] || "Anonymous",
                    author_email: user?.email || "anonymous",
                    author_id: user?._id || user?.id || "anonymous",
                    author_role: isAdmin ? "admin" : "user",
                    verified_contributor: false,
                    created_at: serverTimestamp(),
                    chat_type: "community",
                    pinned: false,
                    reply_to: replyingTo ? {
                        id: replyingTo.id,
                        author: replyingTo.author,
                        content: replyingTo.content
                    } : null
                });
            }

            setMessageContent("");
            setReplyingTo(null);
            setShowEmoji(false);
            removeImage(); // Clear image logic
        } catch (error) {
            console.error("Error sending message:", error);
            alert("Failed to send message.");
        } finally {
            setSending(false);
        }
    };

    // Delete message (Admin OR Owner)
    const handleDeleteMessage = async (messageId, authorEmail) => {
        // Allow if Admin OR if the message belongs to current user
        if (!isAdmin && authorEmail !== user?.email) {
            alert("You can only delete your own messages.");
            return;
        }

        if (!window.confirm("Are you sure you want to delete this message?")) return;

        try {
            await deleteDoc(doc(db, "community_chat", messageId));
            setShowMessageMenu(null);
        } catch (error) {
            console.error("Error deleting message:", error);
            alert("Gagal delete mesej.");
        }
    };

    // Pin/Unpin message (Admin only)
    const handlePinMessage = async (messageId, currentlyPinned) => {
        if (!isAdmin) return;
        try {
            // Unpin all other messages first if pinning new one
            if (!currentlyPinned) {
                const pinnedMessages = messages.filter(m => m.pinned);
                for (const msg of pinnedMessages) {
                    await updateDoc(doc(db, "community_chat", msg.id), { pinned: false });
                }
            }

            await updateDoc(doc(db, "community_chat", messageId), {
                pinned: !currentlyPinned
            });
            setShowMessageMenu(null);
        } catch (error) {
            console.error("Error pinning message:", error);
            alert("Gagal pin mesej.");
        }
    };

    // Group messages by date
    const getMessagesWithDateSeparators = () => {
        const result = [];
        let lastDate = null;

        messages.forEach((message, index) => {
            const msgDate = message.created_at ? formatDate(message.created_at) : 'Unknown';

            if (msgDate !== lastDate) {
                result.push({ type: 'date', date: msgDate, id: `date-${index}` });
                lastDate = msgDate;
            }
            result.push({ type: 'message', ...message });
        });

        return result;
    };

    return (
        <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">

            {/* Compact Header */}
            <div className="bg-white border-b border-gray-200 flex-shrink-0 safe-area-top">
                <div className="px-4 py-3 flex items-center gap-3">
                    <button
                        onClick={() => navigate('/community')}
                        className="p-2 -ml-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                    >
                        <ArrowLeft size={22} />
                    </button>

                    <div className="bg-blue-500 p-2 rounded-full">
                        <MessageSquare size={18} className="text-white" />
                    </div>

                    <div className="flex-1 min-w-0">
                        <h1 className="text-base font-bold text-gray-900 truncate">Community Chat</h1>
                        <p className="text-xs text-gray-500">{messages.length} messages</p>
                    </div>

                    <button
                        onClick={() => setShowWallpaperMenu(true)}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
                        title="Change Wallpaper"
                    >
                        <Palette size={20} />
                    </button>
                </div>
            </div>

            {/* Pinned Message Banner */}
            {pinnedMessage && (
                <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center gap-2 flex-shrink-0 cursor-pointer" onClick={() => {
                    const el = document.getElementById(`msg-${pinnedMessage.id}`);
                    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}>
                    <Pin size={14} className="text-blue-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-blue-800">Pinned Message</p>
                        <p className="text-sm text-blue-900 truncate">{pinnedMessage.content}</p>
                    </div>
                    {isAdmin && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handlePinMessage(pinnedMessage.id, true);
                            }}
                            className="text-blue-600 hover:text-blue-800 p-1"
                        >
                            <X size={16} />
                        </button>
                    )}
                </div>
            )}

            {/* Wallpaper Menu Modal */}
            {showWallpaperMenu && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowWallpaperMenu(false)}>
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                            <h3 className="font-bold text-gray-800">Choose Background</h3>
                            <button onClick={() => setShowWallpaperMenu(false)} className="p-1 hover:bg-gray-100 rounded-full">
                                <X size={20} className="text-gray-500" />
                            </button>
                        </div>
                        <div className="p-4 grid grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto">
                            {wallpapers.map((wp) => (
                                <button
                                    key={wp.id}
                                    onClick={() => handleWallpaperChange(wp.src)}
                                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${currentWallpaper === wp.src ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'}`}
                                >
                                    <img src={wp.src} alt={wp.name} className="w-full h-full object-cover" />
                                    <div className="absolute inset-x-0 bottom-0 bg-black/60 p-1">
                                        <p className="text-[10px] text-white text-center truncate">{wp.name}</p>
                                    </div>
                                    {currentWallpaper === wp.src && (
                                        <div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full p-0.5">
                                            <Star size={10} fill="currentColor" />
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}




            {/* Chat Messages - Full Height Area */}
            <div className="flex-1 relative overflow-hidden bg-[#efe7dd] dark:bg-gray-900">
                {/* Fixed Background Pattern Overlay */}
                <div
                    className="absolute inset-0 z-0 pointer-events-none opacity-[0.15] dark:opacity-[0.1]"
                    style={{
                        backgroundImage: `url(${currentWallpaper})`,
                        backgroundSize: '300px',
                        backgroundRepeat: 'repeat'
                    }}
                />

                {/* Scrollable Content Layer */}
                <div className="absolute inset-0 overflow-y-auto z-10 px-3 py-4">
                    {loading ? (
                        <div className="flex items-center justify-center h-full py-20">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                                <MessageSquare className="w-10 h-10 text-blue-500" />
                            </div>
                            <h3 className="text-lg font-semibold mb-2 text-gray-800">No messages yet</h3>
                            <p className="text-gray-500 text-sm">Be the first to say hello!</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {getMessagesWithDateSeparators().map((item) => {
                                // Date Separator
                                if (item.type === 'date') {
                                    return (
                                        <div key={item.id} className="flex justify-center my-4">
                                            <span className="bg-white/80 backdrop-blur-sm text-gray-500 text-xs font-medium px-4 py-1.5 rounded-full shadow-sm border border-gray-100">
                                                {item.date}
                                            </span>
                                        </div>
                                    );
                                }

                                // Message Bubble
                                const message = item;
                                const isMe = message.author_email === user?.email;
                                const isAdminMsg = message.author_role === 'admin';

                                return (
                                    <motion.div
                                        id={`msg-${message.id}`}
                                        key={message.id}
                                        className={`flex ${isMe ? 'justify-end' : 'justify-start'} items-start gap-1 group relative touch-pan-y ${showMessageMenu === message.id ? 'z-50' : 'z-0'}`}
                                        drag="x"
                                        dragConstraints={{ left: 0, right: 0 }}
                                        dragElastic={0.1}
                                        onDragEnd={(e, info) => {
                                            if (info.offset.x > 50) {
                                                handleReply(message);
                                                // Haptic feedback if available (mobile)
                                                if (navigator.vibrate) navigator.vibrate(50);
                                            }
                                        }}
                                        whileDrag={{ scale: 1.02 }}
                                    >
                                        {/* Swipe Indicator (Hidden by default, visible during drag via CSS logic or just implicit feel) */}

                                        {!isMe && message.verified_contributor && (
                                            <div className="flex flex-col gap-1 items-center mt-6">
                                                <div className="mb-1" title="Verified Contributor">
                                                    <Star size={12} className="fill-yellow-400 text-yellow-400" />
                                                </div>
                                            </div>
                                        )}

                                        <div className="relative flex-shrink-0 mt-6 ml-1 order-last">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setShowMessageMenu(showMessageMenu === message.id ? null : message.id);
                                                }}
                                                className="bg-white shadow-sm rounded-full p-2 border border-gray-200 hover:bg-gray-50 opacity-100 transition-opacity"
                                            >
                                                <MoreVertical size={16} className="text-gray-800" />
                                            </button>

                                            {showMessageMenu === message.id && (
                                                <div
                                                    ref={menuRef}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className={`absolute top-10 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[99999] min-w-[140px] right-0`}
                                                > {/* Fixed z-index and event bubbling */}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleReply(message);
                                                            setShowMessageMenu(null);
                                                        }}
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 text-gray-700"
                                                    >
                                                        <Reply size={14} />
                                                        Reply
                                                    </button>

                                                    {isMe && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEditClick(message);
                                                            }}
                                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 text-gray-700"
                                                        >
                                                            <div className="w-3.5 h-3.5"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg></div>
                                                            {/* SVG Edit Icon verified */}
                                                            Edit
                                                        </button>
                                                    )}

                                                    {(isAdmin || isMe) && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteMessage(message.id, message.author_email);
                                                            }}
                                                            className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                                                        >
                                                            <Trash2 size={14} />
                                                            Delete
                                                        </button>
                                                    )}

                                                    {isAdmin && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handlePinMessage(message.id, message.pinned);
                                                            }}
                                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 text-gray-700 border-t border-gray-100 mt-1 pt-1"
                                                        >
                                                            <Pin size={14} />
                                                            {message.pinned ? 'Unpin' : 'Pin'}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <div className={`max-w-[75%] ${isMe ? 'order-first mr-1' : 'ml-1'}`}>
                                            {!isMe && (
                                                <div className="flex items-center gap-1 mb-1 ml-3">
                                                    <p className={`text-xs font-bold ${isAdminMsg ? 'text-blue-600' : 'text-gray-800'}`}>
                                                        {message.author_name}
                                                        {isAdminMsg && <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded ml-1 font-normal">ADMIN</span>}
                                                    </p>
                                                    {message.verified_contributor && (
                                                        <Star size={12} className="fill-yellow-400 text-yellow-400" />
                                                    )}
                                                </div>
                                            )}

                                            <div className={`relative px-4 py-2.5 rounded-2xl shadow-sm ${isMe
                                                ? 'bg-blue-500 text-white rounded-br-md'
                                                : isAdminMsg
                                                    ? 'bg-white border-2 border-blue-100 text-gray-800 rounded-bl-md'
                                                    : 'bg-white text-gray-800 rounded-bl-md border border-gray-100'
                                                } ${message.pinned ? 'ring-2 ring-yellow-400' : ''}`}>

                                                {/* Image Render */}
                                                {message.imageUrl && (
                                                    <div className="mb-2 -mx-1 mt-1">
                                                        <img
                                                            src={message.imageUrl}
                                                            alt="Attachment"
                                                            className="rounded-lg max-h-[300px] w-full object-cover bg-gray-100 border border-white/20"
                                                            loading="lazy"
                                                            onClick={() => window.open(message.imageUrl, '_blank')}
                                                        />
                                                    </div>
                                                )}

                                                {/* Reply Context */}
                                                {message.reply_to && (
                                                    <div className={`text-xs p-2 mb-2 rounded border-l-4 cursor-pointer hover:opacity-80 transition-opacity ${isMe
                                                        ? 'bg-white/20 border-white/50 text-white'
                                                        : 'bg-gray-100 border-gray-400 text-black'
                                                        }`}
                                                        onClick={() => {
                                                            const el = document.getElementById(`msg-${message.reply_to.id}`);
                                                            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                            el?.classList.add('ring-2', 'ring-offset-2', 'ring-blue-400');
                                                            setTimeout(() => el?.classList.remove('ring-2', 'ring-offset-2', 'ring-blue-400'), 2000);
                                                        }}>
                                                        <p className="font-bold mb-0.5 opacity-90">{message.reply_to.author}</p>
                                                        <p className="truncate opacity-75">{message.reply_to.content || "Image attachment"}</p>
                                                    </div>
                                                )}

                                                {message.pinned && (
                                                    <Pin size={12} className="absolute -top-1 -right-1 text-white bg-yellow-400 rounded-full p-0.5 shadow-sm" />
                                                )}

                                                <p className="text-[15px] leading-relaxed whitespace-pre-wrap break-words">
                                                    {message.content}
                                                    {message.is_edited && <span className={`text-[10px] italic ml-1 ${isMe ? 'text-white/80' : 'text-gray-400'}`}>(edited)</span>}
                                                </p>

                                                <p className={`text-[10px] mt-1 text-right ${isMe ? 'text-blue-100' : 'text-gray-400'
                                                    }`}>
                                                    {formatTime(message.created_at)}
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                            <div ref={messagesEndRef} className="h-2" />
                        </div>
                    )}
                </div>
            </div>

            {/* Input Area - Fixed at Bottom */}
            <div className="flex-shrink-0 bg-white border-t border-gray-200 px-4 py-3 safe-area-bottom">
                {/* Emoji Picker */}
                {showEmoji && (
                    <div className="absolute bottom-20 left-4 right-4 bg-white rounded-2xl shadow-xl p-3 border border-gray-200 z-10">
                        <div className="grid grid-cols-8 gap-2">
                            {['👍', '👎', '❤️', '🔥', '🚀', '💯', '🎯', '📈', '📉', '💡', '⚠️', '✅', '❌', '🤔', '👀', '🙏', '😀', '😂', '😍', '🥳', '😎', '🤑', '💪', '👏'].map((emoji) => (
                                <button
                                    key={emoji}
                                    onClick={() => {
                                        setMessageContent(prev => prev + emoji);
                                        setShowEmoji(false);
                                    }}
                                    className="text-2xl hover:bg-gray-100 rounded-lg p-2 transition-colors active:scale-95"
                                >
                                    {emoji}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex items-center gap-2 w-full relative">

                    {/* Image Preview Overlay */}
                    {imagePreview && (
                        <div className="absolute bottom-full left-0 right-0 p-3 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-sm z-30 mb-[1px] rounded-t-xl">
                            <div className="relative inline-block">
                                <img src={imagePreview} alt="Preview" className="h-20 w-auto rounded-lg border border-gray-300 shadow-sm object-cover" />
                                <button
                                    onClick={removeImage}
                                    className="absolute -top-2 -right-2 bg-gray-800 text-white rounded-full p-1 shadow-md hover:bg-black transition-colors"
                                >
                                    <X size={12} />
                                </button>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">Image selected</p>
                        </div>
                    )}

                    {/* Edit/Reply Preview */}
                    {(replyingTo || editingMessage) && (
                        <div className={`absolute bottom-full left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 border-b shadow-sm px-4 py-3 flex justify-between items-center z-20 translate-y-[1px] ${imagePreview ? 'mb-[110px]' : ''}`}>
                            <div className="flex items-center gap-3 overflow-hidden flex-1">
                                {/* Reply Icon (Visual Anchor) */}
                                <div className="flex-shrink-0 text-blue-500">
                                    {editingMessage ? <div className="w-5" /> : <Reply size={24} className="scale-x-[-1]" />}

                                </div>

                                {/* Vertical Colored Line */}
                                <div className={`w-[3px] h-10 ${editingMessage ? 'bg-orange-500' : 'bg-blue-500'} rounded-full flex-shrink-0`}></div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <p className={`text-xs font-bold leading-tight mb-0.5 ${editingMessage ? 'text-orange-600' : 'text-blue-600'}`}>
                                        {editingMessage ? 'Editing Message' : `Reply to ${replyingTo.author}`}
                                    </p>
                                    <p className="text-[13px] text-gray-600 truncate leading-tight">
                                        {editingMessage ? messageContent : replyingTo.content || "Image attachment"}
                                    </p>
                                </div>
                            </div>

                            {/* Close Button */}
                            <button onClick={() => {
                                setReplyingTo(null);
                                handleCancelEdit();
                            }} className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0 ml-2">
                                <X size={20} className="text-gray-400" />
                            </button>
                        </div>
                    )}

                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className={`flex-shrink-0 p-2 rounded-full transition-all ${imageFile ? 'text-blue-500 bg-blue-50' : 'text-gray-400 hover:text-blue-500 hover:bg-blue-50'}`}
                    >
                        <ImageIcon size={20} />
                    </button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileSelect}
                    />

                    <button
                        onClick={() => setShowEmoji(!showEmoji)}
                        className={`flex-shrink-0 p-2 rounded-full transition-all ${showEmoji ? 'text-blue-500 bg-blue-50' : 'text-gray-400 hover:text-blue-500 hover:bg-blue-50'}`}
                    >
                        <Smile size={20} />
                    </button>

                    <textarea
                        id="chat-input"
                        rows={1}
                        value={messageContent}
                        onChange={(e) => {
                            setMessageContent(e.target.value);
                            e.target.style.height = 'auto';
                            e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                                // Reset height
                                e.target.style.height = 'auto';
                            }
                        }}
                        placeholder={replyingTo ? `Replying to ${replyingTo.author}...` : editingMessage ? "Edit your message..." : "Message (Shift+Enter for new line)..."}
                        className="flex-1 min-w-0 bg-gray-100 border-0 text-gray-900 placeholder-gray-500 rounded-2xl px-4 py-3 text-[15px] focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all resize-none max-h-[120px]"
                        disabled={sending}
                    />

                    <button
                        onClick={() => {
                            handleSend();
                            const el = document.getElementById("chat-input");
                            if (el) el.style.height = 'auto';
                        }}
                        disabled={(!messageContent.trim() && !imageFile) || sending}
                        className="flex-shrink-0 bg-blue-500 hover:bg-blue-600 disabled:opacity-40 disabled:hover:bg-blue-500 text-white p-2.5 rounded-full shadow-md transition-all active:scale-95 h-[42px] w-[42px] flex items-center justify-center self-end"
                    >
                        {sending ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} className="ml-0.5" />}
                    </button>
                </div>
            </div>

            {/* Click outside to close menu */}
            {/* Click outside to close menu - Handled by useEffect now to avoid z-index blocking */
            }
        </div>
    );
}
